import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Planning, PlanningSection, PlanningStep } from "types/planning";
import { PartnerInfos } from "types";
import {
  PLANNING_SECTIONS,
  MODEL_WEEK_COUNTS,
  SectionDef,
} from "../../../utils/planning_constants";
import Icon1 from "@assets/icons/Icon";
import dragHandGif from "@assets/gifs/icons8-glisser-droite.gif";

// ─── Week column computation ─────────────────────────────────────────────────

const FRENCH_MONTHS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

interface WeekColumn {
  index: number; // 0-based column index
  isoWeek: number; // ISO week number (1-53)
  month: number; // 1-12
  monthLabel: string;
  year: number;
  isBlocked: boolean; // August OR ISO week 52 OR ISO week 1
  monday: Date;
}

function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((d.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7,
    )
  );
}

const MODELS_WITHOUT_CONGES = new Set(["12_mois_ordre"]);

function computeWeekColumns(startDate: Date, model: string): WeekColumn[] {
  const n = MODEL_WEEK_COUNTS[model] ?? 48;
  const hasConges = !MODELS_WITHOUT_CONGES.has(model);
  return Array.from({ length: n }, (_, i) => {
    const monday = new Date(startDate.getTime() + i * 7 * 86400000);
    const isoWeek = getISOWeek(monday);
    const month = monday.getMonth() + 1;
    return {
      index: i,
      isoWeek,
      month,
      monthLabel: FRENCH_MONTHS[month - 1],
      year: monday.getFullYear(),
      isBlocked: hasConges && (
        // 4 weeks in August: Mondays falling on Aug 1-28
        (month === 8 && monday.getDate() <= 28) ||
        // 2 winter weeks: last 2 Mondays of December (always Dec 20-31), never spills into January
        (month === 12 && monday.getDate() >= 20)
      ),
      monday,
    };
  });
}

// Group adjacent weeks by month for merged header cells
interface MonthGroup {
  label: string;
  year: number;
  colSpan: number;
}

function buildMonthGroups(cols: WeekColumn[]): MonthGroup[] {
  const groups: MonthGroup[] = [];
  for (const col of cols) {
    const last = groups[groups.length - 1];
    if (last && last.label === col.monthLabel && last.year === col.year) {
      last.colSpan++;
    } else {
      groups.push({ label: col.monthLabel, year: col.year, colSpan: 1 });
    }
  }
  return groups;
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  planning: Planning;
  partners: PartnerInfos[];
  onUpdate: (updated: Planning) => void;
}

export const PlanningTable = ({ planning, partners, onUpdate }: Props) => {
  const startDate = useMemo(() => {
    // Parse YYYY-MM-DD as local time to avoid UTC timezone shift
    const [y, m, d] = planning.start_date.split("T")[0].split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [planning.start_date]);

  const cols = useMemo(
    () => computeWeekColumns(startDate, planning.model),
    [startDate, planning.model],
  );

  const monthGroups = useMemo(() => buildMonthGroups(cols), [cols]);

  // Artisan filter: which artisan IDs are visible
  const allArtisanIds = useMemo(() => {
    const ids = new Set<string>();
    planning.sections.forEach((s) =>
      s.artisan_ids.forEach((id) => ids.add(id)),
    );
    return Array.from(ids);
  }, [planning.sections]);

  const [hiddenArtisans, setHiddenArtisans] = useState<Set<string>>(new Set());

  const toggleHideArtisan = (id: string) => {
    setHiddenArtisans((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Scroll navigation
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const [dragging, setDragging] = useState(false);

  // Drag hint overlay — shown once per browser
  const HINT_KEY = "planning_drag_hint_seen";
  const [hintVisible, setHintVisible] = useState(true);
  const [hintFading, setHintFading] = useState(false);

  const dismissHint = useCallback(() => {
    if (!hintVisible) return;
    setHintFading(true);
    localStorage.setItem(HINT_KEY, "1");
    setTimeout(() => setHintVisible(false), 400);
  }, [hintVisible]);

  useEffect(() => {
    if (!hintVisible) return;
    const t = setTimeout(dismissHint, 4500);
    return () => clearTimeout(t);
  }, [hintVisible, dismissHint]);

  useEffect(() => {
    const onWindowMouseUp = () => {
      isDragging.current = false;
      setDragging(false);
    };
    window.addEventListener("mouseup", onWindowMouseUp);
    return () => window.removeEventListener("mouseup", onWindowMouseUp);
  }, []);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    dismissHint();
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    if (Math.abs(dx) > 5) {
      hasDragged.current = true;
      setDragging(true);
      if (scrollRef.current)
        scrollRef.current.scrollLeft = dragScrollLeft.current - dx;
    }
  };

  const stopDragging = () => {
    isDragging.current = false;
    setDragging(false);
  };

  // Debounced save
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [localPlanning, setLocalPlanning] = useState<Planning>(planning);

  // Sync if parent sends a new planning
  useEffect(() => {
    setLocalPlanning(planning);
  }, [planning.id, planning.updated_at]);

  const scheduleSave = useCallback(
    (updated: Planning) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        onUpdate(updated);
      }, 800);
    },
    [onUpdate],
  );

  function toggleWeek(
    sectionKey: string,
    stepKey: string,
    artisanId: string,
    weekIndex: number,
  ) {
    setLocalPlanning((prev) => {
      const updated: Planning = {
        ...prev,
        sections: prev.sections.map((section) => {
          if (section.section_key !== sectionKey) return section;
          return {
            ...section,
            steps: section.steps.map((step) => {
              if (step.step_key !== stepKey) return step;
              const currentWeeks = step.colored_weeks[artisanId] || [];
              const hasWeek = currentWeeks.includes(weekIndex);
              return {
                ...step,
                colored_weeks: {
                  ...step.colored_weeks,
                  [artisanId]: hasWeek
                    ? currentWeeks.filter((w) => w !== weekIndex)
                    : [...currentWeeks, weekIndex],
                },
              };
            }),
          };
        }),
      };
      scheduleSave(updated);
      return updated;
    });
  }

  const handleToggleWeek = (
    sectionKey: string,
    stepKey: string,
    artisanId: string,
    weekIndex: number,
  ) => {
    if (hasDragged.current) return;
    toggleWeek(sectionKey, stepKey, artisanId, weekIndex);
  };

  const partnerMap = useMemo(() => {
    const m: Record<string, PartnerInfos> = {};
    partners.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [partners]);

  // ─── Cell width ───────────────────────────────────────────────────────────
  const COL_W = 28; // px per week column
  const LABEL_W = 260; // px for the left label column

  return (
    <div className="flex flex-col gap-4">
      {/* Artisan filter */}
      {allArtisanIds.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-h4 text-neutral-400">Filtrer :</span>
          {allArtisanIds.map((id) => {
            const p = partnerMap[id];
            const name = p?.company_name || p?.display_name || id;
            const hidden = hiddenArtisans.has(id);
            return (
              <button
                key={id}
                onClick={() => toggleHideArtisan(id)}
                className={`px-3 py-1 rounded-full text-h4 border transition-all ${
                  hidden
                    ? "bg-white text-neutral-400 border-neutral-200"
                    : "bg-neutral-900 text-white border-neutral-900"
                }`}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}

      {/* Scrollable table */}
      <div className="relative">
        {hintVisible && (
          <>
            <div
              className={`absolute top-16 left-1/2 -translate-x-1/2 z-10 pointer-events-none transition-opacity duration-300 ${hintFading ? "opacity-0" : "opacity-100"}`}
            >
              <div className="bg-white text-neutral-800 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-2xl border border-neutral-200">
                <img src={dragHandGif} alt="" className="w-8 h-8" />
                <span className="text-h3 font-medium whitespace-nowrap">
                  Cliquez et maintenez pour vous déplacer horizontalement
                </span>
              </div>
            </div>
          </>
        )}
        <div
          ref={scrollRef}
          className={`overflow-auto max-h-[72vh] rounded-xl border border-neutral-200 ${dragging ? "cursor-grabbing select-none" : "cursor-grab"}`}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={stopDragging}
          onMouseLeave={stopDragging}
        >
          <table
            className="border-separate border-spacing-0"
            style={{ minWidth: LABEL_W + cols.length * COL_W }}
          >
            <thead className="sticky top-0 z-30">
              {/* Row 1 — months (merged) */}
              <tr>
                <td
                  className="sticky left-0 z-40 bg-neutral-100 border border-neutral-200 px-2 py-1 text-h4 text-neutral-500"
                  style={{ width: LABEL_W, minWidth: LABEL_W }}
                >
                  Mois
                </td>
                {monthGroups.map((g, i) => (
                  <td
                    key={i}
                    className="relative z-30 bg-neutral-100 border border-neutral-200 text-h4 font-medium text-center"
                    colSpan={g.colSpan}
                    style={{ minWidth: g.colSpan * COL_W }}
                  >
                    {g.label}
                  </td>
                ))}
              </tr>

              {/* Row 3 — ISO week numbers */}
              <tr>
                <td
                  className="sticky left-0 z-40 bg-neutral-50 border border-neutral-200 px-2 py-1 text-h4 text-neutral-400"
                  style={{ width: LABEL_W, minWidth: LABEL_W }}
                >
                  Semaine
                </td>
                {cols.map((col) => (
                  <td
                    key={col.index}
                    className={`relative z-30 border border-neutral-200 text-center text-h4 font-medium ${
                      col.isBlocked
                        ? "bg-gray-300 text-gray-400"
                        : "bg-neutral-50 text-neutral-600"
                    }`}
                    style={{ width: COL_W, minWidth: COL_W }}
                  >
                    {col.isoWeek}
                  </td>
                ))}
              </tr>
            </thead>

            <tbody>
              {PLANNING_SECTIONS.map((sectionDef) => {
                const sectionData = localPlanning.sections.find(
                  (s) => s.section_key === sectionDef.key,
                );
                if (!sectionData) return null;

                const visibleArtisans = sectionData.artisan_ids.filter(
                  (id) => !hiddenArtisans.has(id),
                );

                return (
                  <SectionRows
                    key={sectionDef.key}
                    sectionDef={sectionDef}
                    sectionData={sectionData}
                    visibleArtisanIds={visibleArtisans}
                    partnerMap={partnerMap}
                    cols={cols}
                    colW={COL_W}
                    labelW={LABEL_W}
                    onToggleWeek={handleToggleWeek}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── SectionRows sub-component ────────────────────────────────────────────────

interface SectionRowsProps {
  sectionDef: SectionDef;
  sectionData: PlanningSection;
  visibleArtisanIds: string[];
  partnerMap: Record<string, PartnerInfos>;
  cols: WeekColumn[];
  colW: number;
  labelW: number;
  onToggleWeek: (
    sectionKey: string,
    stepKey: string,
    artisanId: string,
    weekIndex: number,
  ) => void;
}

function SectionRows({
  sectionDef,
  sectionData,
  visibleArtisanIds,
  partnerMap,
  cols,
  colW,
  labelW,
  onToggleWeek,
}: SectionRowsProps) {
  const artisanNames = visibleArtisanIds.map(
    (id) => partnerMap[id]?.company_name || partnerMap[id]?.display_name || id,
  );

  return (
    <>
      {/* Section header row */}
      <tr>
        <td
          className={`sticky left-0 z-10 px-2 py-1 text-h3 font-bold ${sectionDef.headerBg} ${sectionDef.headerText}`}
          style={{ width: labelW, minWidth: labelW }}
        >
          {sectionDef.label}
          {artisanNames.length > 0 && (
            <span className="ml-2 font-normal text-h4 opacity-80">
              {artisanNames.join(", ")}
            </span>
          )}
        </td>
        {cols.map((col) => (
          <td
            key={col.index}
            className={`border-b border-neutral-200 ${
              col.isBlocked
                ? "bg-gray-300"
                : sectionDef.headerBg + " opacity-30"
            }`}
            style={{ width: colW }}
          />
        ))}
      </tr>

      {/* Step rows (one per artisan per step) */}
      {sectionDef.steps.map((stepDef) => {
        const stepData = sectionData.steps.find(
          (s) => s.step_key === stepDef.key,
        );

        if (visibleArtisanIds.length === 0) {
          // No artisans assigned — show empty row
          return (
            <tr key={stepDef.key}>
              <td
                className="sticky left-0 z-10 px-3 py-1 text-h4 text-neutral-500 bg-white border-b border-neutral-100"
                style={{ width: labelW, minWidth: labelW }}
              >
                {stepDef.label}
              </td>
              {cols.map((col) => (
                <td
                  key={col.index}
                  className={`border-b border-neutral-100 ${
                    col.isBlocked ? "bg-gray-200" : "bg-neutral-50"
                  }`}
                  style={{ width: colW }}
                />
              ))}
            </tr>
          );
        }

        return visibleArtisanIds.map((artisanId, aIdx) => {
          const artisanName =
            partnerMap[artisanId]?.company_name ||
            partnerMap[artisanId]?.display_name ||
            artisanId;
          const coloredWeeks = stepData?.colored_weeks[artisanId] || [];

          return (
            <tr key={`${stepDef.key}-${artisanId}`}>
              <td
                className="sticky left-0 z-10 px-3 py-1 bg-white border-b border-neutral-100"
                style={{ width: labelW, minWidth: labelW }}
              >
                <div className="flex flex-col">
                  {aIdx === 0 && (
                    <span className="text-h4 text-neutral-700">
                      {stepDef.label}
                    </span>
                  )}
                  {visibleArtisanIds.length > 1 && (
                    <span className="text-h4 text-neutral-400 italic">
                      {artisanName}
                    </span>
                  )}
                </div>
              </td>
              {cols.map((col) => {
                const isColored = coloredWeeks.includes(col.index);
                return (
                  <td
                    key={col.index}
                    onClick={() =>
                      !col.isBlocked &&
                      onToggleWeek(
                        sectionData.section_key,
                        stepDef.key,
                        artisanId,
                        col.index,
                      )
                    }
                    className={`border-b border-neutral-100 transition-colors ${
                      col.isBlocked
                        ? "bg-gray-200 cursor-not-allowed"
                        : isColored
                          ? `${sectionDef.cellColor} cursor-pointer`
                          : "bg-white hover:bg-neutral-50 cursor-pointer"
                    }`}
                    style={{ width: colW, height: 24 }}
                    title={
                      col.isBlocked
                        ? "Congés"
                        : `S${col.isoWeek} — ${col.monthLabel}`
                    }
                  />
                );
              })}
            </tr>
          );
        });
      })}
    </>
  );
}
